/**
 * Research Hub page — /research
 * Completely redesigned with bento grid, count-up stats, marquee ticker, dataset preview
 */
import React, { useState, useEffect, useRef } from "react";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

// ─── useReveal ───────────────────────────────────────────────────────────────
function useReveal(delay = 0): { ref: React.RefObject<HTMLDivElement | null>; style: React.CSSProperties } {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(28px)",
    transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
  };

  return { ref, style };
}

// ─── useCountUp ──────────────────────────────────────────────────────────────
function useCountUp(target: number, duration = 1400): { val: number; ref: React.RefObject<HTMLDivElement | null> } {
  const ref = useRef<HTMLDivElement>(null);
  const [val, setVal] = useState(0);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // cubic ease-out
            const eased = 1 - Math.pow(1 - progress, 3);
            setVal(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { val, ref };
}

// ─── Dataset cards data ───────────────────────────────────────────────────────
const datasets = [
  {
    title: "B2B SERP Feature Landscape 2026",
    scale: "200 keywords · 6 features",
    category: "SERP Features",
    insight: "61% of B2B SERPs carry at least one zero-click feature",
    href: "/data/b2b-serp-features-2026",
  },
  {
    title: "AI Overview by Buyer Funnel Stage 2026",
    scale: "90 keywords · 3 stages",
    category: "AI Overviews",
    insight: "TOFU queries trigger AI Overviews 88% of the time vs 12% for BOFU",
    href: "/data/ai-overview-by-funnel-stage",
  },
  {
    title: "B2B Organic CTR by SERP Position 2026",
    scale: "100+ keywords",
    category: "CTR Data",
    insight: "Position 1 CTR drops from 28% to 19% when an AI Overview is present",
    href: "/data/b2b-ctr-by-position-2026",
  },
  {
    title: "Domain Rating Benchmarks by Industry",
    scale: "550 data points",
    category: "Authority",
    insight: "SaaS keywords require a median DR of 72 to appear in the top 10",
    href: "/data/domain-rating-benchmarks-b2b",
  },
  {
    title: "B2B Keyword Intent Distribution 2026",
    scale: "200+ keywords · 4 intents",
    category: "Keyword Research",
    insight: "55% of B2B keywords are informational — highest AI Overview exposure",
    href: "/data/b2b-keyword-intent-distribution",
  },
  {
    title: "Content Length Benchmarks for B2B 2026",
    scale: "100 keywords",
    category: "Content",
    insight: "Top-ranking commercial B2B pages average 2,340 words",
    href: "/data/content-length-benchmarks-b2b",
  },
  {
    title: "B2B Search Volume Trends 2024 vs 2026",
    scale: "150 keywords",
    category: "Search Trends",
    insight: "Informational B2B queries declined 18% YoY as AI answers more queries",
    href: "/data/b2b-keyword-search-volume-trends",
  },
  {
    title: "Backlink Requirements to Rank Top 5 in B2B",
    scale: "275 pages analyzed",
    category: "Link Building",
    insight: "Median top-5 SaaS page has 47 referring domains — lower than most expect",
    href: "/data/backlink-benchmarks-b2b",
  },
];

const marqueeItems = [
  "B2B SERP Feature Landscape",
  "AI Overview by Funnel Stage",
  "CTR by SERP Position",
  "Domain Rating Benchmarks",
  "Keyword Intent Distribution",
  "Content Length Benchmarks",
  "Search Volume Trends 2024–2026",
  "CPC vs Organic Value",
  "Question Keywords & AI Overviews",
  "Backlink Benchmarks",
  "SEO Benchmarks by Industry",
];

// ─── DatasetCard ─────────────────────────────────────────────────────────────
const DatasetCard = React.forwardRef<HTMLDivElement, {
  title: string;
  scale: string;
  category: string;
  insight: string;
  href: string;
  revealStyle: React.CSSProperties;
}>(function DatasetCard({ title, scale, category, insight, href, revealStyle }, ref) {
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref} style={revealStyle}>
      <a
        href={href}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "block",
          textDecoration: "none",
          background: "white",
          border: `1px solid ${hov ? "rgba(87,215,226,0.35)" : "rgba(0,0,0,0.08)"}`,
          borderRadius: "14px",
          overflow: "hidden",
          transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
          boxShadow: hov ? "0 6px 24px rgba(87,215,226,0.1)" : "none",
          transform: hov ? "translateY(-2px)" : "none",
          padding: "24px 26px",
        }}
      >
        {/* Top row: category + arrow */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <span style={{
            display: "inline-block",
            padding: "3px 9px",
            borderRadius: "4px",
            background: "rgba(87,215,226,0.08)",
            border: "1px solid rgba(87,215,226,0.18)",
            color: "#0b8a96",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: ff,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
          }}>
            {category}
          </span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: hov ? 1 : 0.3, transition: "opacity 0.2s, transform 0.2s", transform: hov ? "translate(2px, -2px)" : "none" }}>
            <path d="M3 13L13 3M13 3H7M13 3v6" stroke="#0b8a96" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Title */}
        <div style={{ color: "#0a0a0a", fontSize: "15px", fontWeight: 700, fontFamily: ff, lineHeight: 1.35, marginBottom: "10px" }}>
          {title}
        </div>

        {/* Key insight */}
        <div style={{
          color: hov ? "#0b8a96" : "rgba(0,0,0,0.55)",
          fontSize: "13px",
          fontFamily: ff,
          lineHeight: 1.55,
          marginBottom: "14px",
          transition: "color 0.2s",
          fontStyle: "italic",
        }}>
          "{insight}"
        </div>

        {/* Scale */}
        <div style={{ color: "rgba(0,0,0,0.3)", fontSize: "12px", fontFamily: ff, letterSpacing: "0.02em" }}>
          {scale}
        </div>
      </a>
    </div>
  );
});

// ─── BentoCard3DTilt ─────────────────────────────────────────────────────────
function use3DTilt(): {
  tiltStyle: React.CSSProperties;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
} {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ rx: -y * 6, ry: x * 6 });
  };

  const onMouseLeave = () => setTilt({ rx: 0, ry: 0 });

  const tiltStyle: React.CSSProperties = {
    transform: `perspective(800px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
    transition: "transform 0.15s ease",
  };

  return { tiltStyle, onMouseMove, onMouseLeave };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ResearchHub() {
  const isMobile = useIsMobile();

  useSeoMeta({
    title: "SEO Research Hub — Data, Studies & Benchmarks — Indexed",
    description: "SEO research from Indexed: original B2B studies, keyword datasets, CTR benchmarks, and AI Overview data, all free for marketers who want real evidence.",
    canonical: "/research",
    ogType: "website",
    jsonLd: [{
      "@type": "CollectionPage",
      "name": "Indexed SEO Research Hub",
      "description": "Original B2B SEO research from Indexed — datasets, studies, and benchmarks covering AI Overviews, CTR, domain authority, and search performance.",
      "url": "https://www.joinindexed.com/research",
      "publisher": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" }
    }],
  });

  // Count-up hooks
  const count11 = useCountUp(11);
  const count200 = useCountUp(200);
  const count3 = useCountUp(3);

  // Bento card reveals
  const revealBento0 = useReveal(0);
  const revealBento1 = useReveal(100);
  const revealBento2 = useReveal(200);

  // Dataset card reveals (staggered in pairs)
  const revealDs0 = useReveal(0);
  const revealDs1 = useReveal(80);
  const revealDs2 = useReveal(160);
  const revealDs3 = useReveal(240);
  const revealDs4 = useReveal(0);
  const revealDs5 = useReveal(80);
  const revealDs6 = useReveal(160);
  const revealDs7 = useReveal(240);

  const dsReveals = [revealDs0, revealDs1, revealDs2, revealDs3, revealDs4, revealDs5, revealDs6, revealDs7];

  // 3D tilt for bento cards
  const tilt0 = use3DTilt();
  const tilt1 = use3DTilt();
  const tilt2 = use3DTilt();


  // Doubled marquee items for seamless loop
  const allMarquee = [...marqueeItems, ...marqueeItems];

  return (
    <SiteLayout>
      {/* ── Keyframe animations ───────────────────────────────────────────── */}
      <style>{`
        @keyframes blobDrift {
          0%,100%{transform:translate(0,0) scale(1)}
          33%{transform:translate(40px,-30px) scale(1.07)}
          66%{transform:translate(-25px,18px) scale(0.94)}
        }
        @keyframes blobDrift2 {
          0%,100%{transform:translate(0,0) scale(1)}
          33%{transform:translate(-35px,22px) scale(1.05)}
          66%{transform:translate(28px,-18px) scale(0.96)}
        }
        @keyframes marqueeScroll {
          from{transform:translateX(0)}
          to{transform:translateX(-50%)}
        }
        @keyframes livePulse {
          0%,100%{opacity:1;transform:scale(1)}
          50%{opacity:0.4;transform:scale(0.85)}
        }
        @keyframes fadeUp {
          from{opacity:0;transform:translateY(24px)}
          to{opacity:1;transform:translateY(0)}
        }
      `}</style>

      {/* ── 1. Hero ───────────────────────────────────────────────────────── */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "160px 20px 60px" : "200px 72px 72px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Dot grid overlay */}
        <div style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
          zIndex: 0,
        }} />

        {/* Blob 1 */}
        <div style={{
          position: "absolute",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(87,215,226,0.12) 0%, transparent 70%)",
          top: "-200px",
          right: "-100px",
          animation: "blobDrift 14s ease-in-out infinite",
          zIndex: 0,
          pointerEvents: "none",
        }} />

        {/* Blob 2 */}
        <div style={{
          position: "absolute",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(87,215,226,0.07) 0%, transparent 70%)",
          bottom: "-100px",
          left: "-150px",
          animation: "blobDrift2 18s ease-in-out infinite",
          zIndex: 0,
          pointerEvents: "none",
        }} />

        {/* Content */}
        <div style={{
          maxWidth: "1100px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
          display: isMobile ? "block" : "flex",
          flexDirection: "row",
          gap: "48px",
          alignItems: "center",
        }}>
          {/* Left */}
          <div style={{ flex: 1 }}>
            <div style={{
              color: cyan,
              fontFamily: ff,
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "20px",
            }}>
              Evidence-based · B2B Search
            </div>

            <h1 style={{
              color: "white",
              fontSize: isMobile ? "34px" : "clamp(40px,4.5vw,56px)",
              fontWeight: 800,
              fontFamily: ff,
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              margin: 0,
            }}>
              Original research on how{" "}
              <span style={{ color: cyan }}>B2B search</span>{" "}
              is changing.
            </h1>

            <p style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "18px",
              fontFamily: ff,
              lineHeight: 1.65,
              maxWidth: "500px",
              marginTop: "20px",
              marginBottom: 0,
            }}>
              Built on real keyword data — not opinions. Benchmarks, datasets,
              and studies from Indexed.
            </p>
          </div>

          {/* Right — floating stat cards (desktop only) */}
          {!isMobile && (
            <div style={{
              flexShrink: 0,
              width: "340px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginLeft: "-20px",
            }}>
              {[
                { num: "11", label: "Original datasets", delay: "0.1s" },
                { num: "200+", label: "B2B keywords analyzed", delay: "0.25s" },
                { num: "31%", label: "avg CTR drop at #1 with AI Overview", delay: "0.4s" },
              ].map(({ num, label, delay }) => (
                <div
                  key={label}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    borderRadius: "16px",
                    padding: "20px 24px",
                    backdropFilter: "blur(12px)",
                    animation: `fadeUp 0.5s ease both`,
                    animationDelay: delay,
                  }}
                >
                  <div style={{
                    color: cyan,
                    fontSize: "38px",
                    fontWeight: 800,
                    fontFamily: ff,
                    lineHeight: 1,
                  }}>
                    {num}
                  </div>
                  <div style={{
                    color: "rgba(255,255,255,0.45)",
                    fontSize: "13px",
                    fontFamily: ff,
                    marginTop: "6px",
                  }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── 2. Count-up stats strip ───────────────────────────────────────── */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "0 20px 60px" : "0 72px 72px",
      }}>
        <div style={{
          maxWidth: "1100px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 0,
          borderTop: "1px solid rgba(255,255,255,0.07)",
          paddingTop: "48px",
        }}>
          {/* Stat 1 */}
          <div
            ref={count11.ref}
            style={{
              textAlign: "center",
              padding: isMobile ? "0 8px" : "0 40px",
              borderRight: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <div style={{
              color: cyan,
              fontSize: isMobile ? "40px" : "clamp(48px,5vw,64px)",
              fontWeight: 800,
              fontFamily: ff,
              lineHeight: 1,
            }}>
              {count11.val}
            </div>
            <div style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: isMobile ? "12px" : "14px",
              fontFamily: ff,
              marginTop: "8px",
            }}>
              datasets
            </div>
          </div>

          {/* Stat 2 */}
          <div
            ref={count200.ref}
            style={{
              textAlign: "center",
              padding: isMobile ? "0 8px" : "0 40px",
              borderRight: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <div style={{
              color: cyan,
              fontSize: isMobile ? "40px" : "clamp(48px,5vw,64px)",
              fontWeight: 800,
              fontFamily: ff,
              lineHeight: 1,
            }}>
              {count200.val}+
            </div>
            <div style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: isMobile ? "12px" : "14px",
              fontFamily: ff,
              marginTop: "8px",
            }}>
              keywords
            </div>
          </div>

          {/* Stat 3 */}
          <div
            ref={count3.ref}
            style={{
              textAlign: "center",
              padding: isMobile ? "0 8px" : "0 40px",
            }}
          >
            <div style={{
              color: cyan,
              fontSize: isMobile ? "40px" : "clamp(48px,5vw,64px)",
              fontWeight: 800,
              fontFamily: ff,
              lineHeight: 1,
            }}>
              {count3.val}
            </div>
            <div style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: isMobile ? "12px" : "14px",
              fontFamily: ff,
              marginTop: "8px",
            }}>
              studies published
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Marquee ticker ─────────────────────────────────────────────── */}
      <section style={{
        background: "rgba(255,255,255,0.02)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "18px 0",
        overflow: "hidden",
        position: "relative",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 0,
        }}>
          {/* Live indicator */}
          <div style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "0 20px",
            background: "#0a0a0a",
            borderRight: "1px solid rgba(255,255,255,0.06)",
            height: "100%",
            zIndex: 2,
            position: "relative",
          }}>
            <div style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#2dd4bf",
              animation: "livePulse 2s ease-in-out infinite",
              flexShrink: 0,
            }} />
            {!isMobile && (
              <span style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: "12px",
                fontFamily: ff,
                whiteSpace: "nowrap",
              }}>
                Live data · March 2026
              </span>
            )}
          </div>

          {/* Scrolling items */}
          <div style={{ overflow: "hidden", flex: 1 }}>
            <div style={{
              display: "flex",
              width: "max-content",
              animation: "marqueeScroll 28s linear infinite",
            }}>
              {allMarquee.map((item, i) => (
                <span key={i} style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "0 14px",
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "13px",
                  fontFamily: ff,
                  whiteSpace: "nowrap",
                }}>
                  {item}
                  <span style={{ color: cyan, opacity: 0.7 }}>·</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Bento grid ─────────────────────────────────────────────────── */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "40px 20px" : "60px 72px",
      }}>
        <div style={{
          maxWidth: "1100px",
          margin: "0 auto",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          gridTemplateColumns: isMobile ? undefined : "2fr 1fr",
          gridTemplateRows: isMobile ? undefined : "auto auto",
          gap: "20px",
        }}>

          {/* Card 1 — Studies (row span 2) */}
          <div ref={revealBento0.ref} style={{
            ...revealBento0.style,
            gridRow: isMobile ? undefined : "1 / 3",
          }}>
            <div
              {...(isMobile ? {} : {
                onMouseMove: tilt0.onMouseMove,
                onMouseLeave: tilt0.onMouseLeave,
              })}
              style={{
                ...(isMobile ? {} : tilt0.tiltStyle),
                position: "relative",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                background: "#111",
                overflow: "hidden",
                cursor: "pointer",
                padding: isMobile ? "32px 24px" : "44px 40px",
                height: isMobile ? "auto" : "100%",
                transition: "border-color 0.2s, background 0.2s",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.2)";
                e.currentTarget.style.background = "rgba(87,215,226,0.03)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.background = "#111";
              }}
            >
              {/* Badge */}
              <div style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "rgba(87,215,226,0.08)",
                border: "1px solid rgba(87,215,226,0.15)",
                borderRadius: "6px",
                padding: "4px 10px",
                color: cyan,
                fontSize: "11px",
                fontFamily: ff,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}>
                Original Research
              </div>

              {/* Icon */}
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "14px",
                background: "rgba(87,215,226,0.06)",
                border: "1px solid rgba(87,215,226,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
                flexShrink: 0,
              }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path d="M5 6h12l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" stroke={cyan} strokeWidth="1.6" opacity="0.85"/>
                  <path d="M17 6v5h5" stroke={cyan} strokeWidth="1.4" opacity="0.5"/>
                  <path d="M8 15h6M8 19h4" stroke={cyan} strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
                </svg>
              </div>

              <h2 style={{
                color: "white",
                fontSize: isMobile ? "22px" : "28px",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.2,
                marginBottom: "12px",
                marginTop: 0,
              }}>
                Studies
              </h2>

              <p style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: "15px",
                fontFamily: ff,
                lineHeight: 1.65,
                marginBottom: "28px",
                marginTop: 0,
                flex: 1,
              }}>
                In-depth research reports on SEO, AI search, and B2B organic
                growth. Each study is backed by keyword-level data, not
                industry speculation.
              </p>

              <a href="/studies" style={{
                color: cyan,
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: ff,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "32px",
              }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
              >
                Browse all studies →
              </a>

              {/* Study topic tags */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {["AI Overviews", "CTR Impact", "SERP Features", "Link Benchmarks"].map(tag => (
                  <span key={tag} style={{ padding: "4px 10px", borderRadius: "20px", background: "rgba(87,215,226,0.07)", border: "1px solid rgba(87,215,226,0.12)", color: cyan, fontSize: "11px", fontFamily: ff, fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2 — Data */}
          <div ref={revealBento1.ref} style={{
            ...revealBento1.style,
            gridColumn: isMobile ? undefined : "2",
            gridRow: isMobile ? undefined : "1",
          }}>
            <div
              {...(isMobile ? {} : {
                onMouseMove: tilt1.onMouseMove,
                onMouseLeave: tilt1.onMouseLeave,
              })}
              style={{
                ...(isMobile ? {} : tilt1.tiltStyle),
                position: "relative",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                background: "#111",
                overflow: "hidden",
                cursor: "pointer",
                padding: isMobile ? "32px 24px" : "32px 28px",
                transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.2)";
                e.currentTarget.style.background = "rgba(87,215,226,0.03)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.background = "#111";
              }}
            >
              {/* Badge */}
              <div style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "rgba(87,215,226,0.08)",
                border: "1px solid rgba(87,215,226,0.15)",
                borderRadius: "6px",
                padding: "4px 10px",
                color: cyan,
                fontSize: "11px",
                fontFamily: ff,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}>
                11 Datasets
              </div>

              {/* Icon */}
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "14px",
                background: "rgba(87,215,226,0.06)",
                border: "1px solid rgba(87,215,226,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
              }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <ellipse cx="14" cy="8" rx="9" ry="3.5" stroke={cyan} strokeWidth="1.6" opacity="0.85"/>
                  <path d="M5 8v6c0 1.933 4.03 3.5 9 3.5s9-1.567 9-3.5V8" stroke={cyan} strokeWidth="1.4" opacity="0.6"/>
                  <path d="M5 14v6c0 1.933 4.03 3.5 9 3.5s9-1.567 9-3.5v-6" stroke={cyan} strokeWidth="1.4" opacity="0.35"/>
                </svg>
              </div>

              <h2 style={{
                color: "white",
                fontSize: "22px",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.2,
                marginBottom: "10px",
                marginTop: 0,
              }}>
                Data
              </h2>

              <p style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: "14px",
                fontFamily: ff,
                lineHeight: 1.65,
                marginBottom: "20px",
                marginTop: 0,
              }}>
                Raw keyword datasets, CTR benchmarks, and SERP performance data
                — updated quarterly.
              </p>

              <a href="/data" style={{
                color: cyan,
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: ff,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
              >
                Browse all data →
              </a>
            </div>
          </div>

          {/* Card 3 — Key Finding */}
          <div ref={revealBento2.ref} style={{
            ...revealBento2.style,
            gridColumn: isMobile ? undefined : "2",
            gridRow: isMobile ? undefined : "2",
          }}>
            <div
              {...(isMobile ? {} : {
                onMouseMove: tilt2.onMouseMove,
                onMouseLeave: tilt2.onMouseLeave,
              })}
              style={{
                ...(isMobile ? {} : tilt2.tiltStyle),
                border: "1px solid rgba(87,215,226,0.12)",
                borderRadius: "20px",
                background: "linear-gradient(135deg, rgba(87,215,226,0.06) 0%, rgba(87,215,226,0.02) 100%)",
                overflow: "hidden",
                cursor: "pointer",
                padding: isMobile ? "32px 24px" : "32px 28px",
                transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.25)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.12)";
              }}
            >
              <div style={{
                color: cyan,
                fontSize: "48px",
                fontWeight: 800,
                fontFamily: ff,
                lineHeight: 1,
                marginBottom: "12px",
              }}>
                −31%
              </div>

              <p style={{
                color: "rgba(255,255,255,0.55)",
                fontSize: "14px",
                fontFamily: ff,
                lineHeight: 1.6,
                marginBottom: "20px",
                marginTop: 0,
              }}>
                avg position 1 CTR drop when AI Overview is present
              </p>

              <a href="/studies/ctr-study-2026" style={{
                color: cyan,
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: ff,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
              >
                Read the CTR study →
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* ── 5. Dataset preview (white) ────────────────────────────────────── */}
      <section style={{
        background: "#ffffff",
        padding: isMobile ? "40px 20px 60px" : "72px 72px 80px",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2 style={{
            color: "#0a0a0a",
            fontSize: "30px",
            fontWeight: 800,
            fontFamily: ff,
            marginBottom: "8px",
            marginTop: 0,
          }}>
            Explore the data
          </h2>
          <p style={{
            color: "rgba(0,0,0,0.45)",
            fontSize: "16px",
            fontFamily: ff,
            marginBottom: "40px",
            marginTop: 0,
          }}>
            Raw datasets from Indexed research — updated quarterly
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "12px",
          }}>
            {datasets.map((ds, i) => (
              <DatasetCard
                key={ds.href}
                ref={dsReveals[i]?.ref}
                title={ds.title}
                scale={ds.scale}
                category={ds.category}
                insight={ds.insight}
                href={ds.href}
                revealStyle={dsReveals[i]?.style ?? {}}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Dark CTA ───────────────────────────────────────────────────── */}
      <section style={{
        background: "#0c0c0c",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: isMobile ? "60px 20px" : "80px 72px",
      }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{
            color: "white",
            fontSize: isMobile ? "26px" : "36px",
            fontWeight: 700,
            fontFamily: ff,
            lineHeight: 1.15,
            marginBottom: "16px",
            marginTop: 0,
          }}>
            Want this analysis for your keywords?
          </h2>
          <p style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "17px",
            fontFamily: ff,
            lineHeight: 1.6,
            marginBottom: "32px",
            marginTop: 0,
          }}>
            We run custom AI Overview exposure audits for B2B companies. See
            exactly which of your keywords are affected — and what it's costing
            you in clicks.
          </p>
          <a
            href={STRATEGIST_URL}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "16px 32px",
              background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
              borderRadius: "88px",
              border: "1px solid #57d7e2",
              boxShadow: "0px 4px 20px rgba(87,215,226,0.25)",
              textDecoration: "none",
              color: "#0a0c0c",
              fontSize: "15px",
              fontWeight: 700,
              fontFamily: ff,
              transition: "opacity 0.2s, transform 0.2s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.opacity = "0.88";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Get a custom analysis
          </a>
        </div>
      </section>
    </SiteLayout>
  );
}
